// src/components/layout/ComingSoonLayout.tsx - Lightweight Version
import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Clock, Calendar } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

interface ComingSoonLayoutProps {
  title: string;
  subtitle: string;
  description: string;
  icon?: React.ReactNode;
  estimatedDate?: string;
}

const ComingSoonLayout: React.FC<ComingSoonLayoutProps> = ({
  title,
  subtitle,
  description,
  icon,
  estimatedDate = "Coming Soon"
}) => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Navbar />
      
      {/* Main Content - Simplified */}
      <main className="pt-20 pb-16">
        <section className="min-h-[80vh] flex items-center justify-center">
          {/* Simple background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/30 dark:from-slate-900/50 dark:via-transparent dark:to-blue-900/10" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            {/* Icon - Simplified */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <div className="h-20 w-20 sm:h-24 sm:w-24 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                {icon || <Clock className="h-10 w-10 sm:h-12 sm:w-12 text-white" />}
              </div>
            </motion.div>

            {/* Title - Simplified */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-6"
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-white mb-4 leading-tight">
                {title}
              </h1>
              <div className="h-1 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mx-auto mb-6 w-24 sm:w-32" />
            </motion.div>

            {/* Subtitle */}
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-6"
            >
              {subtitle}
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              {description}
            </motion.p>

            {/* Coming Soon Badge - Simplified */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mb-12"
            >
              <div className="inline-flex items-center space-x-2 bg-blue-100 dark:bg-blue-900/30 px-6 py-3 rounded-full border border-blue-200 dark:border-blue-700/50">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <span className="text-blue-600 dark:text-blue-400 font-semibold">{estimatedDate}</span>
              </div>
            </motion.div>

            {/* Additional Info - Simplified */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto"
            >
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-xl">
                <Calendar className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Segera Hadir</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Dalam pengembangan</p>
              </div>
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-xl">
                <Clock className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Update Rutin</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Pantau terus ya!</p>
              </div>
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-xl">
                <div className="h-6 w-6 text-teal-500 mx-auto mb-2 text-lg">⚙️</div>
                <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Dalam Proses</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Sedang dikerjakan</p>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
export default memo(ComingSoonLayout);